import { useEffect, useState, useCallback } from "react";
import { api, apiErrorMessage } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import UrlHealthSection from "../components/UrlHealthSection";
import DeveloperSection from "../components/admin/DeveloperSection";
import InvitesSection from "../components/admin/InvitesSection";
import { Plus, Pencil, Trash2, MousePointerClick, UserPlus2, LineChart as LineIcon, Package, Users2, Copy, Link2, Info, Target as TargetIcon, ExternalLink, Wallet, Save, Check, Share2, ChevronDown, ChevronRight } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";

const BLUE = "#0055FF";
const GREEN = "#00A36C";
const YELLOW = "#FFB800";
const CHART_COLORS = [BLUE, GREEN, YELLOW, "#7C3AED", "#EF4444", "#0EA5E9", "#EC4899", "#F59E0B"];

// ----- Product Form -----
const EMPTY = {
  type: "banking", category: "credit_card", title: "", subtitle: "", description: "",
  provider: "", image_url: "", price: "", original_price: "", apr: "", interest_rate: "", tenure: "",
  features: "", lead_mode: "form_then_redirect", redirect_url: "",
  ref_param_name: "subid", forward_utms: true,
  form_fields: "name,email,phone", cta_label: "Apply Now", active: true,
  meta_pixel_id: "",
  capture_image_url: "", brand_color: "",
};

function ProductForm({ open, onOpenChange, onSaved, initial }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initial) {
      setForm({
        ...EMPTY,
        ...initial,
        price: initial.price ?? "",
        original_price: initial.original_price ?? "",
        features: (initial.features || []).join(", "),
        form_fields: (initial.form_fields || []).join(","),
      });
    } else {
      setForm(EMPTY);
    }
  }, [initial, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        type: form.type,
        category: form.category,
        title: form.title,
        subtitle: form.subtitle,
        description: form.description,
        provider: form.provider,
        image_url: form.image_url,
        price: form.price !== "" ? parseFloat(form.price) : null,
        original_price: form.original_price !== "" ? parseFloat(form.original_price) : null,
        apr: form.apr || null,
        interest_rate: form.interest_rate || null,
        tenure: form.tenure || null,
        features: form.features ? form.features.split(",").map((x) => x.trim()).filter(Boolean) : [],
        lead_mode: form.lead_mode,
        redirect_url: form.redirect_url,
        ref_param_name: form.ref_param_name || "subid",
        forward_utms: !!form.forward_utms,
        form_fields: form.form_fields ? form.form_fields.split(",").map((x) => x.trim()).filter(Boolean) : [],
        cta_label: form.cta_label,
        active: form.active,
        meta_pixel_id: (form.meta_pixel_id || "").trim() || null,
        capture_image_url: (form.capture_image_url || "").trim(),
        brand_color: (form.brand_color || "").trim(),
      };
      if (initial?.id) {
        await api.put(`/products/${initial.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product created");
      }
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">{initial ? "Edit product" : "New product"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-4 mt-2">
          <div className="col-span-2 grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Type</Label>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger data-testid="pf-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="banking">Banking</SelectItem>
                  <SelectItem value="ecom">Commerce</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Category</Label>
              <Input data-testid="pf-category" value={form.category} onChange={(e) => set("category", e.target.value)} placeholder="credit_card, loan, electronics…" />
            </div>
          </div>
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Title</Label>
            <Input required data-testid="pf-title" value={form.title} onChange={(e) => set("title", e.target.value)} />
          </div>
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Subtitle</Label>
            <Input data-testid="pf-subtitle" value={form.subtitle} onChange={(e) => set("subtitle", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Provider</Label>
            <Input required data-testid="pf-provider" value={form.provider} onChange={(e) => set("provider", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Image URL</Label>
            <Input data-testid="pf-image" value={form.image_url} onChange={(e) => set("image_url", e.target.value)} />
          </div>
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Description</Label>
            <Textarea data-testid="pf-description" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} />
          </div>
          {form.type === "ecom" ? (
            <>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Price</Label>
                <Input type="number" step="0.01" data-testid="pf-price" value={form.price} onChange={(e) => set("price", e.target.value)} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Original price</Label>
                <Input type="number" step="0.01" data-testid="pf-original-price" value={form.original_price} onChange={(e) => set("original_price", e.target.value)} />
              </div>
            </>
          ) : (
            <>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">APR</Label>
                <Input data-testid="pf-apr" value={form.apr} onChange={(e) => set("apr", e.target.value)} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Interest rate</Label>
                <Input data-testid="pf-interest" value={form.interest_rate} onChange={(e) => set("interest_rate", e.target.value)} />
              </div>
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Tenure</Label>
                <Input data-testid="pf-tenure" value={form.tenure} onChange={(e) => set("tenure", e.target.value)} />
              </div>
            </>
          )}
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Features (comma separated)</Label>
            <Input data-testid="pf-features" value={form.features} onChange={(e) => set("features", e.target.value)} placeholder="Zero fee, 5X rewards, Lounge access" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Lead capture mode</Label>
            <Select value={form.lead_mode} onValueChange={(v) => set("lead_mode", v)}>
              <SelectTrigger data-testid="pf-lead-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="form_only">Form only</SelectItem>
                <SelectItem value="redirect_only">Redirect only</SelectItem>
                <SelectItem value="form_then_redirect">Form → Redirect</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">CTA label</Label>
            <Input data-testid="pf-cta-label" value={form.cta_label} onChange={(e) => set("cta_label", e.target.value)} />
          </div>
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Meta / Facebook Pixel ID</Label>
            <Input
              data-testid="pf-meta-pixel-id"
              value={form.meta_pixel_id}
              onChange={(e) => set("meta_pixel_id", e.target.value)}
              placeholder="e.g. 1645926196902525 (leave empty to disable)"
              inputMode="numeric"
            />
            <p className="text-[11px] text-neutral-500 mt-1">Fires a PageView when the phone-capture dialog opens for this product, then a Lead event on submit. One pixel per product — use different ids per lender to attribute cost to the right ad account.</p>
          </div>
          <div className="col-span-2 rounded-xl border border-black/10 bg-neutral-50/50 p-4 space-y-3">
            <div>
              <div className="text-xs uppercase tracking-[0.15em] text-neutral-500 font-semibold">Mobile capture dialog</div>
              <p className="text-[11px] text-neutral-500 mt-1">
                Optional branding shown on the phone-capture step for banking &amp; commerce products.
                If both fields are empty, the dialog falls back to the partner's favicon on a default blue tile.
              </p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Capture image URL</Label>
              <Input
                data-testid="pf-capture-image-url"
                value={form.capture_image_url}
                onChange={(e) => set("capture_image_url", e.target.value)}
                placeholder="https://cdn.example.com/creditsea-banner.png"
              />
              <p className="text-[11px] text-neutral-500 mt-1">
                Full-width banner (3:1 ratio recommended). Overrides the branded-tile fallback.
              </p>
              {form.capture_image_url && (
                <div className="mt-2 rounded-lg overflow-hidden border border-black/10 bg-white" style={{ aspectRatio: "3 / 1", maxWidth: 320 }}>
                  <img
                    src={form.capture_image_url}
                    alt="Capture preview"
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Brand colour</Label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="color"
                  data-testid="pf-brand-color-picker"
                  value={form.brand_color || "#0055FF"}
                  onChange={(e) => set("brand_color", e.target.value.toUpperCase())}
                  className="w-11 h-11 rounded-lg border border-black/10 cursor-pointer bg-white"
                  aria-label="Brand colour picker"
                />
                <Input
                  data-testid="pf-brand-color-hex"
                  value={form.brand_color}
                  onChange={(e) => set("brand_color", e.target.value)}
                  placeholder="#0055FF"
                  maxLength={7}
                  className="font-mono-accent uppercase"
                />
                {form.brand_color && (
                  <button
                    type="button"
                    onClick={() => set("brand_color", "")}
                    className="text-[11px] text-neutral-500 hover:text-neutral-900 underline underline-offset-4"
                    data-testid="pf-brand-color-clear"
                  >
                    Clear
                  </button>
                )}
              </div>
              <p className="text-[11px] text-neutral-500 mt-1">
                Used for the dialog's brand tile background, CTA button, and the initials fallback when no favicon renders.
              </p>
            </div>
          </div>
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Master URL (Partner)</Label>
            <Input data-testid="pf-redirect-url" value={form.redirect_url} onChange={(e) => set("redirect_url", e.target.value)} placeholder="https://partner.com/apply?campaign_id=ABC" />
            <p className="text-[11px] text-neutral-500 mt-1">Paste the partner-supplied tracking URL. We append our reference id (below) so both sides can reconcile.</p>
            {form.redirect_url && (
              <div className="mt-2 p-2 bg-neutral-50 border border-black/5 rounded text-[11px] break-all">
                <span className="badge-mono text-neutral-500 mr-1">Preview:</span>
                <span className="font-mono-accent text-neutral-700">
                  {(() => {
                    try {
                      const u = new URL(form.redirect_url);
                      u.searchParams.set("p1", "HITCH-20260714-000001");
                      u.searchParams.set("p2", "12345");
                      if (form.ref_param_name) u.searchParams.set(form.ref_param_name, "<ref_id>");
                      return u.toString();
                    } catch {
                      return "Invalid URL — must start with https:// and be a valid absolute URL.";
                    }
                  })()}
                </span>
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Ref param name</Label>
            <Input data-testid="pf-ref-param" value={form.ref_param_name} onChange={(e) => set("ref_param_name", e.target.value)} placeholder="subid" />
            <p className="text-[11px] text-neutral-500 mt-1">Common values: <span className="font-mono-accent">subid</span>, <span className="font-mono-accent">aff_id</span>, <span className="font-mono-accent">click_id</span>, <span className="font-mono-accent">mp_ref</span></p>
          </div>
          <div className="flex items-end pb-2">
            <label className="flex items-center gap-2 text-sm text-neutral-700 select-none">
              <input
                type="checkbox"
                data-testid="pf-forward-utms"
                checked={!!form.forward_utms}
                onChange={(e) => set("forward_utms", e.target.checked)}
                className="w-4 h-4 rounded border-black/20"
              />
              Forward customer UTMs to partner
            </label>
          </div>
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Form fields (comma separated)</Label>
            <Input data-testid="pf-form-fields" value={form.form_fields} onChange={(e) => set("form_fields", e.target.value)} placeholder="name,email,phone,income" />
          </div>
          <div className="col-span-2 flex items-center justify-end gap-2 mt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="pf-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="pf-save" className="bg-black hover:bg-[#0055FF] text-white">
              {saving ? "Saving…" : (initial ? "Save changes" : "Create product")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ----- Analytics section -----
function Analytics() {
  const [summary, setSummary] = useState(null);
  const [timeseries, setTimeseries] = useState([]);
  const [bySource, setBySource] = useState([]);
  const [byCampaign, setByCampaign] = useState([]);
  const [byProduct, setByProduct] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/analytics/summary"),
      api.get("/analytics/timeseries", { params: { days: 14 } }),
      api.get("/analytics/by-dimension", { params: { dimension: "utm_source" } }),
      api.get("/analytics/by-dimension", { params: { dimension: "utm_campaign" } }),
      api.get("/analytics/by-dimension", { params: { dimension: "product_id" } }),
    ]).then(([s, t, src, camp, prod]) => {
      setSummary(s.data);
      setTimeseries(t.data);
      setBySource(src.data);
      setByCampaign(camp.data);
      setByProduct(prod.data);
    });
  }, []);

  const stats = summary ? [
    { icon: MousePointerClick, label: "Total clicks", value: summary.total_clicks },
    { icon: UserPlus2, label: "Leads captured", value: summary.total_leads },
    { icon: LineIcon, label: "Conversion rate", value: `${summary.conversion_rate}%` },
    { icon: Package, label: "Products live", value: summary.total_products },
  ] : [];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {stats.map((s) => (
          <div key={s.label} className="bg-white border border-black/8 rounded-lg p-5" data-testid={`stat-${s.label.replace(/\s+/g, "-").toLowerCase()}`}>
            <s.icon className="w-4 h-4 text-neutral-400" strokeWidth={1.8} />
            <div className="mt-3 text-xs uppercase tracking-[0.15em] text-neutral-500">{s.label}</div>
            <div className="font-display text-3xl font-bold tracking-tight mt-1">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-black/8 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-lg font-semibold tracking-tight">Clicks & leads · last 14 days</div>
          <Badge variant="secondary" className="badge-mono">Real-time</Badge>
        </div>
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <AreaChart data={timeseries}>
              <defs>
                <linearGradient id="gClicks" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={BLUE} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={BLUE} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gLeads" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={GREEN} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={GREEN} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#999" />
              <YAxis tick={{ fontSize: 11 }} stroke="#999" />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="clicks" stroke={BLUE} fill="url(#gClicks)" strokeWidth={2} />
              <Area type="monotone" dataKey="leads" stroke={GREEN} fill="url(#gLeads)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white border border-black/8 rounded-lg p-6">
          <div className="font-display text-lg font-semibold tracking-tight mb-4">By UTM source</div>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={bySource.slice(0, 8)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="key" tick={{ fontSize: 11 }} stroke="#999" />
                <YAxis tick={{ fontSize: 11 }} stroke="#999" />
                <Tooltip />
                <Bar dataKey="clicks" fill={BLUE} radius={[4, 4, 0, 0]} />
                <Bar dataKey="leads" fill={GREEN} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white border border-black/8 rounded-lg p-6">
          <div className="font-display text-lg font-semibold tracking-tight mb-4">Campaigns</div>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={byCampaign.slice(0, 6)} dataKey="clicks" nameKey="key" outerRadius={90} innerRadius={45}>
                  {byCampaign.slice(0, 6).map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="bg-white border border-black/8 rounded-lg p-6">
        <div className="font-display text-lg font-semibold tracking-tight mb-4">Top products by clicks</div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product ID</TableHead>
              <TableHead className="text-right">Clicks</TableHead>
              <TableHead className="text-right">Leads</TableHead>
              <TableHead className="text-right">CTR</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {byProduct.slice(0, 10).map((r) => (
              <TableRow key={r.key} data-testid={`product-row-${r.key}`}>
                <TableCell className="font-mono-accent text-xs">{r.key.slice(0, 8)}…</TableCell>
                <TableCell className="text-right">{r.clicks}</TableCell>
                <TableCell className="text-right">{r.leads}</TableCell>
                <TableCell className="text-right">{r.ctr}%</TableCell>
              </TableRow>
            ))}
            {!byProduct.length && (
              <TableRow><TableCell colSpan={4} className="text-center text-neutral-500 py-6">No data yet — clicks will populate here.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ----- Products management -----
function ProductsSection() {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/products/mine/list");
    setProducts(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await api.delete(`/products/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight">Products</h2>
          <p className="text-sm text-neutral-500">Create and manage listings</p>
        </div>
        <Button data-testid="new-product-btn" onClick={() => { setEditing(null); setOpen(true); }} className="bg-black hover:bg-[#0055FF] text-white">
          <Plus className="w-4 h-4 mr-1" /> New product
        </Button>
      </div>

      <div className="bg-white border border-black/8 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Provider</TableHead>
              <TableHead>Lead mode</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.map((p) => (
              <TableRow key={p.id} data-testid={`prod-row-${p.id}`}>
                <TableCell className="font-medium">{p.title}</TableCell>
                <TableCell><Badge variant="secondary" className="capitalize">{p.type}</Badge></TableCell>
                <TableCell className="text-neutral-600">{p.provider}</TableCell>
                <TableCell><Badge variant="outline" className="badge-mono">{p.lead_mode}</Badge></TableCell>
                <TableCell>
                  <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${p.active ? "bg-[#00A36C]" : "bg-neutral-300"}`} />
                  {p.active ? "Active" : "Inactive"}
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" data-testid={`edit-${p.id}`} onClick={() => { setEditing(p); setOpen(true); }}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" data-testid={`delete-${p.id}`} onClick={() => del(p.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {!products.length && <TableRow><TableCell colSpan={6} className="text-center text-neutral-500 py-8">No products yet</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <ProductForm open={open} onOpenChange={setOpen} onSaved={load} initial={editing} />
    </div>
  );
}

// ----- Leads section -----
function LeadsSection() {
  const [leads, setLeads] = useState([]);
  useEffect(() => { api.get("/leads").then((r) => setLeads(r.data)); }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight">Leads</h2>
          <p className="text-sm text-neutral-500">Applications with full UTM attribution</p>
        </div>
        <Badge variant="secondary" className="badge-mono" data-testid="leads-count">{leads.length} total</Badge>
      </div>
      <div className="bg-white border border-black/8 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Product</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Campaign</TableHead>
              <TableHead>Ref ID (partner reconciliation)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {leads.map((l) => (
              <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                <TableCell className="text-xs text-neutral-500 font-mono-accent">{new Date(l.created_at).toLocaleString()}</TableCell>
                <TableCell className="font-medium">{l.product_title}</TableCell>
                <TableCell>
                  <div className="text-sm">{l.name || "—"}</div>
                  <div className="text-xs text-neutral-500">{l.email || l.phone || ""}</div>
                </TableCell>
                <TableCell><Badge variant="outline">{l.utm_source || "direct"}</Badge></TableCell>
                <TableCell className="text-sm">{l.utm_campaign || "—"}</TableCell>
                <TableCell className="font-mono-accent text-[11px] text-neutral-600" title={l.id}>
                  {l.ref_param_name ? `${l.ref_param_name}=` : ""}{l.id.slice(0, 12)}…
                </TableCell>
              </TableRow>
            ))}
            {!leads.length && <TableRow><TableCell colSpan={6} className="text-center text-neutral-500 py-8">No leads yet</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ----- Partners section (admin only) -----
function PartnersSection() {
  const [partners, setPartners] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/auth/partners");
    setPartners(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/auth/partners", form);
      toast.success("Partner created");
      setOpen(false);
      setForm({ email: "", password: "", name: "" });
      load();
    } catch (err) { toast.error(apiErrorMessage(err)); }
    setSaving(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight">Partners</h2>
          <p className="text-sm text-neutral-500">Banks & merchants publishing on the marketplace</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-partner-btn" className="bg-black hover:bg-[#0055FF] text-white">
              <Plus className="w-4 h-4 mr-1" /> Add partner
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>New partner</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label>Name</Label>
                <Input data-testid="partner-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <Label>Email</Label>
                <Input data-testid="partner-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <Label>Password</Label>
                <Input data-testid="partner-password" type="text" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </div>
              <Button type="submit" disabled={saving} data-testid="partner-submit" className="w-full bg-black hover:bg-[#0055FF] text-white">
                {saving ? "Creating…" : "Create partner"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-white border border-black/8 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {partners.map((p) => (
              <TableRow key={p.id} data-testid={`partner-row-${p.id}`}>
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell className="text-neutral-600">{p.email}</TableCell>
                <TableCell><Badge variant="secondary">{p.role}</Badge></TableCell>
              </TableRow>
            ))}
            {!partners.length && <TableRow><TableCell colSpan={3} className="text-center text-neutral-500 py-8">No partners yet</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ----- Campaigns / UTM Builder section -----
const UTM_DOCS = [
  {
    param: "utm_source",
    required: true,
    purpose: "Identifies where the traffic came from — the platform, publisher or referrer.",
    examples: ["google", "facebook", "newsletter", "partner_hdfc", "policybazaar_app"],
    tip: "Use lowercase, no spaces. Keep the vocabulary consistent across all campaigns (e.g. always 'facebook', never 'FB' one day and 'Facebook' the next).",
  },
  {
    param: "utm_medium",
    required: true,
    purpose: "Identifies the marketing medium — the type of channel.",
    examples: ["cpc", "email", "social", "banner", "affiliate", "sms", "referral"],
    tip: "Stick to standard values: cpc, email, social, display, referral, organic. Helps analytics tools group traffic correctly.",
  },
  {
    param: "utm_campaign",
    required: true,
    purpose: "The individual campaign, product launch or promotion this link belongs to.",
    examples: ["diwali_2026", "hdfc_ergo_optima_launch", "fastag_paytm_q1", "term_life_max_may"],
    tip: "Include quarter/month + product/theme for easy reporting. Avoid PII.",
  },
  {
    param: "utm_term",
    required: false,
    purpose: "The paid search keyword or targeting term this ad is bidding on.",
    examples: ["health+insurance", "best+credit+card", "car+insurance+delhi"],
    tip: "Populated automatically by Google Ads if you use {keyword}. Optional for social/display.",
  },
  {
    param: "utm_content",
    required: false,
    purpose: "Distinguishes variants of the same campaign — creatives, placements or A/B versions.",
    examples: ["hero_banner", "footer_cta", "story_ad_v2", "10off_variant_a"],
    tip: "Use this to A/B test — same source/medium/campaign but different content.",
  },
];

const DATA_COLLECTED = [
  { field: "session_id", desc: "Unique browser session id. Generated on landing; stable across pages." },
  { field: "utm_source · medium · campaign · term · content", desc: "Captured from URL and stored first-touch — never overwritten by later visits." },
  { field: "referrer", desc: "document.referrer at the time of landing/click." },
  { field: "landing_url", desc: "Full URL of the first page the user opened." },
  { field: "current_url", desc: "URL at the moment of the CTA click." },
  { field: "user_agent", desc: "Browser + device + OS string." },
  { field: "ip", desc: "Client IP (respects X-Forwarded-For)." },
  { field: "created_at", desc: "UTC timestamp for every event/session/lead/click." },
];

function buildTrackedUrl(base, c) {
  try {
    const u = new URL(c.destination_path.startsWith("http") ? c.destination_path : base + (c.destination_path.startsWith("/") ? c.destination_path : "/" + c.destination_path));
    if (c.utm_source) u.searchParams.set("utm_source", c.utm_source);
    if (c.utm_medium) u.searchParams.set("utm_medium", c.utm_medium);
    if (c.utm_campaign) u.searchParams.set("utm_campaign", c.utm_campaign);
    if (c.utm_term) u.searchParams.set("utm_term", c.utm_term);
    if (c.utm_content) u.searchParams.set("utm_content", c.utm_content);
    return u.toString();
  } catch {
    return "";
  }
}

const CAMP_EMPTY = {
  name: "", product_id: "", destination_path: "/",
  utm_source: "", utm_medium: "", utm_campaign: "", utm_term: "", utm_content: "",
};

function CampaignsSection() {
  const [products, setProducts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [form, setForm] = useState(CAMP_EMPTY);
  const [saving, setSaving] = useState(false);
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const load = useCallback(async () => {
    const [p, c] = await Promise.all([api.get("/products/mine/list"), api.get("/campaigns")]);
    setProducts(p.data);
    setCampaigns(c.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onProductChange = (id) => {
    if (id === "custom") {
      set("product_id", "");
      set("destination_path", "/marketplace");
      return;
    }
    set("product_id", id);
    set("destination_path", `/product/${id}`);
  };

  const preview = buildTrackedUrl(origin, form);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.product_id) delete payload.product_id;
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await api.post("/campaigns", payload);
      toast.success("Campaign link saved");
      setForm(CAMP_EMPTY);
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed — select the link manually.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this campaign link?")) return;
    try { await api.delete(`/campaigns/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="font-display text-2xl font-bold tracking-tight">Campaigns &amp; UTM Builder</h2>
        <p className="text-sm text-neutral-500">Generate trackable links, share them with partners and see live click / lead counts.</p>
      </div>

      {/* Builder + Preview */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-black/8 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Link2 className="w-4 h-4 text-[#0055FF]" />
            <div className="font-display text-lg font-semibold tracking-tight">Build a campaign link</div>
          </div>
          <form onSubmit={save} className="space-y-4" data-testid="utm-builder-form">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Campaign name (internal)</Label>
              <Input required data-testid="utm-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Diwali 2026 · Facebook · Health hero" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Destination</Label>
              <Select value={form.product_id || "custom"} onValueChange={onProductChange}>
                <SelectTrigger data-testid="utm-destination"><SelectValue placeholder="Pick a product or custom path" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">Custom path…</SelectItem>
                  {products.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(form.product_id === "" || form.product_id === undefined) && (
                <Input
                  className="mt-2"
                  data-testid="utm-custom-path"
                  value={form.destination_path}
                  onChange={(e) => set("destination_path", e.target.value)}
                  placeholder="/marketplace?type=banking&category=fastag"
                />
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">utm_source *</Label>
                <Input required data-testid="utm-source" value={form.utm_source} onChange={(e) => set("utm_source", e.target.value.trim().toLowerCase())} placeholder="google" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">utm_medium *</Label>
                <Input required data-testid="utm-medium" value={form.utm_medium} onChange={(e) => set("utm_medium", e.target.value.trim().toLowerCase())} placeholder="cpc" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">utm_campaign *</Label>
              <Input required data-testid="utm-campaign" value={form.utm_campaign} onChange={(e) => set("utm_campaign", e.target.value.trim().toLowerCase())} placeholder="diwali_2026_health" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">utm_term</Label>
                <Input data-testid="utm-term" value={form.utm_term} onChange={(e) => set("utm_term", e.target.value)} placeholder="health+insurance" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">utm_content</Label>
                <Input data-testid="utm-content" value={form.utm_content} onChange={(e) => set("utm_content", e.target.value)} placeholder="hero_banner_a" />
              </div>
            </div>
            <Button type="submit" disabled={saving} data-testid="utm-save" className="w-full h-11 bg-black hover:bg-[#0055FF] text-white">
              {saving ? "Saving…" : "Save campaign link"}
            </Button>
          </form>
        </div>

        <div className="bg-white border border-black/8 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <TargetIcon className="w-4 h-4 text-[#00A36C]" />
            <div className="font-display text-lg font-semibold tracking-tight">Live preview</div>
          </div>
          <div className="bg-neutral-50 border border-black/8 rounded-md p-4 font-mono-accent text-xs break-all min-h-[80px]" data-testid="utm-preview">
            {preview || <span className="text-neutral-400">Fill the fields on the left to preview your tracked URL…</span>}
          </div>
          <div className="mt-4 flex gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={!preview}
              onClick={() => copy(preview)}
              data-testid="utm-preview-copy"
              className="flex-1"
            >
              <Copy className="w-4 h-4 mr-1.5" /> Copy
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={!preview}
              onClick={() => window.open(preview, "_blank", "noopener,noreferrer")}
              data-testid="utm-preview-test"
            >
              <ExternalLink className="w-4 h-4 mr-1.5" /> Test
            </Button>
          </div>

          <div className="mt-6 space-y-2 text-xs text-neutral-500">
            <div className="flex items-start gap-2"><Info className="w-3.5 h-3.5 mt-0.5 text-[#0055FF]" /> All 5 UTM params flow into <span className="font-semibold text-neutral-700">first-touch</span> attribution on the landing page.</div>
            <div className="flex items-start gap-2"><Info className="w-3.5 h-3.5 mt-0.5 text-[#0055FF]" /> Values are lowercased automatically to prevent duplicates in reports.</div>
            <div className="flex items-start gap-2"><Info className="w-3.5 h-3.5 mt-0.5 text-[#0055FF]" /> Existing session UTM is preserved; ties every subsequent click/lead back to this campaign.</div>
          </div>
        </div>
      </div>

      {/* Saved campaigns */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-xl font-semibold tracking-tight">Saved campaign links</h3>
          <Badge variant="secondary" className="badge-mono" data-testid="campaigns-count">{campaigns.length} total</Badge>
        </div>
        <div className="bg-white border border-black/8 rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Source / Medium</TableHead>
                <TableHead>Campaign</TableHead>
                <TableHead className="text-right">Clicks</TableHead>
                <TableHead className="text-right">Leads</TableHead>
                <TableHead>Link</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {campaigns.map((c) => {
                const url = buildTrackedUrl(origin, c);
                return (
                  <TableRow key={c.id} data-testid={`campaign-row-${c.id}`}>
                    <TableCell>
                      <div className="font-medium">{c.name}</div>
                      <div className="text-xs text-neutral-500 font-mono-accent">{c.owner_name} · {c.owner_role}</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">{c.utm_source} <span className="text-neutral-400">/</span> {c.utm_medium}</div>
                      {c.utm_content && <div className="text-xs text-neutral-500">{c.utm_content}</div>}
                    </TableCell>
                    <TableCell className="text-sm">{c.utm_campaign}</TableCell>
                    <TableCell className="text-right font-medium">{c.clicks}</TableCell>
                    <TableCell className="text-right font-medium">{c.leads}</TableCell>
                    <TableCell className="max-w-[260px]">
                      <div className="text-[11px] text-neutral-500 font-mono-accent truncate" title={url}>{url}</div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" data-testid={`campaign-copy-${c.id}`} onClick={() => copy(url)}><Copy className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" data-testid={`campaign-delete-${c.id}`} onClick={() => remove(c.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!campaigns.length && <TableRow><TableCell colSpan={7} className="text-center text-neutral-500 py-8">No campaign links yet — build one above.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* UTM Docs */}
      <div>
        <div className="mb-3">
          <h3 className="font-display text-xl font-semibold tracking-tight">UTM management reference</h3>
          <p className="text-sm text-neutral-500">What each parameter means, examples, and best practices.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {UTM_DOCS.map((d) => (
            <div key={d.param} className="bg-white border border-black/8 rounded-lg p-5" data-testid={`utm-doc-${d.param}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="font-mono-accent text-sm font-semibold text-[#0055FF]">{d.param}</span>
                {d.required
                  ? <Badge className="badge-mono bg-black text-white hover:bg-black">Required</Badge>
                  : <Badge variant="secondary" className="badge-mono">Optional</Badge>}
              </div>
              <p className="text-sm text-neutral-700">{d.purpose}</p>
              <div className="mt-3">
                <div className="text-[11px] uppercase tracking-[0.15em] text-neutral-500 mb-1.5">Examples</div>
                <div className="flex flex-wrap gap-1.5">
                  {d.examples.map((e) => (
                    <span key={e} className="font-mono-accent text-[11px] bg-neutral-100 border border-black/8 rounded px-2 py-0.5">{e}</span>
                  ))}
                </div>
              </div>
              <div className="mt-3 text-xs text-neutral-500 leading-relaxed"><span className="font-medium text-neutral-700">Tip: </span>{d.tip}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Data we collect */}
      <div>
        <div className="mb-3">
          <h3 className="font-display text-xl font-semibold tracking-tight">Fields collected per event</h3>
          <p className="text-sm text-neutral-500">What lands in your analytics for every session, click and lead.</p>
        </div>
        <div className="bg-white border border-black/8 rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Field</TableHead>
                <TableHead>Description</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {DATA_COLLECTED.map((r) => (
                <TableRow key={r.field}>
                  <TableCell className="font-mono-accent text-sm text-[#0055FF] whitespace-nowrap">{r.field}</TableCell>
                  <TableCell className="text-sm text-neutral-700">{r.desc}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

// ----- Callbacks section (admin only) -----
function CallbacksSection() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/callback").then((r) => setRows(r.data)); }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight">Callback requests</h2>
          <p className="text-sm text-neutral-500">Homepage quote form submissions</p>
        </div>
        <Badge variant="secondary" className="badge-mono" data-testid="callbacks-count">{rows.length} total</Badge>
      </div>
      <div className="bg-white border border-black/8 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>City / Age</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Campaign</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`callback-row-${r.id}`}>
                <TableCell className="text-xs text-neutral-500 font-mono-accent">{new Date(r.created_at).toLocaleString()}</TableCell>
                <TableCell><Badge variant="secondary" className="capitalize">{(r.category || "").replace("_", " ")}</Badge></TableCell>
                <TableCell>
                  <div className="text-sm">{r.name || "—"}</div>
                  <div className="text-xs text-neutral-500">{r.phone || r.email || ""}</div>
                </TableCell>
                <TableCell className="text-sm">{r.city || "—"}{r.age ? ` · ${r.age}` : ""}</TableCell>
                <TableCell><Badge variant="outline">{r.utm_source || "direct"}</Badge></TableCell>
                <TableCell className="text-sm">{r.utm_campaign || "—"}</TableCell>
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={6} className="text-center text-neutral-500 py-8">No callback requests yet</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ----- Legal pages editor (admin only) -----
const LEGAL_PAGES = [
  { slug: "privacy", label: "Privacy Policy" },
  { slug: "terms", label: "Terms & Conditions" },
  { slug: "grievance", label: "Grievance Redressal" },
  { slug: "refund", label: "Refund & Cancellation" },
  { slug: "disclaimer", label: "Disclaimer" },
];

function LegalSection() {
  const [selected, setSelected] = useState("privacy");
  const [page, setPage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);

  const load = useCallback(async (slug) => {
    try {
      const { data } = await api.get(`/legal/${slug}`);
      setPage(data);
    } catch {
      setPage({ slug, title: LEGAL_PAGES.find((p) => p.slug === slug)?.label || slug, body_md: "" });
    }
  }, []);

  useEffect(() => { load(selected); }, [selected, load]);

  const save = async () => {
    if (!page) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/legal/${selected}`, { title: page.title, body_md: page.body_md });
      setPage(data);
      toast.success("Legal page updated");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-display text-2xl font-bold tracking-tight">Legal pages</h2>
        <p className="text-sm text-neutral-500">Edit customer-facing policies. Markdown supported (## heading, - list, **bold**, [link](url)).</p>
      </div>

      <div className="grid md:grid-cols-[220px_1fr] gap-4">
        <div className="bg-white border border-black/8 rounded-lg p-3 h-fit">
          {LEGAL_PAGES.map((p) => (
            <button
              key={p.slug}
              data-testid={`legal-select-${p.slug}`}
              onClick={() => setSelected(p.slug)}
              className={`w-full text-left text-sm px-3 py-2 rounded-md transition-colors ${
                selected === p.slug ? "bg-black text-white" : "text-neutral-700 hover:bg-neutral-100"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="bg-white border border-black/8 rounded-lg p-6">
          {!page ? <div className="text-neutral-400">Loading…</div> : (
            <div className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-xs uppercase tracking-[0.15em] text-neutral-500">
                  <span className="font-mono-accent">/legal/{selected}</span>
                  {page.updated_at && <span> · updated {new Date(page.updated_at).toLocaleString()} · by {page.updated_by || "system"}</span>}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="legal-preview-toggle"
                    onClick={() => setPreview((p) => !p)}
                  >
                    {preview ? "Edit" : "Preview"}
                  </Button>
                  <a
                    href={`/legal/${selected}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-[#0055FF] hover:underline"
                  >
                    Open public page ↗
                  </a>
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Title</Label>
                <Input
                  data-testid="legal-title"
                  value={page.title}
                  onChange={(e) => setPage({ ...page, title: e.target.value })}
                />
              </div>

              {preview ? (
                <div className="border border-black/8 rounded-lg p-6 bg-neutral-50 min-h-[400px]">
                  <ReactMarkdown
                    components={{
                      h2: ({ children }) => <h2 className="font-display text-xl font-bold tracking-tight mt-6 mb-2">{children}</h2>,
                      h3: ({ children }) => <h3 className="font-display text-base font-semibold tracking-tight mt-4 mb-1.5">{children}</h3>,
                      p: ({ children }) => <p className="text-neutral-700 mb-3">{children}</p>,
                      ul: ({ children }) => <ul className="list-disc pl-6 space-y-1 mb-3 text-neutral-700">{children}</ul>,
                      ol: ({ children }) => <ol className="list-decimal pl-6 space-y-1 mb-3 text-neutral-700">{children}</ol>,
                      strong: ({ children }) => <strong className="font-semibold text-neutral-900">{children}</strong>,
                      a: ({ children, href }) => <a href={href} className="text-[#0055FF] underline underline-offset-4">{children}</a>,
                    }}
                  >
                    {page.body_md}
                  </ReactMarkdown>
                </div>
              ) : (
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Body (Markdown)</Label>
                  <Textarea
                    data-testid="legal-body"
                    rows={22}
                    value={page.body_md}
                    onChange={(e) => setPage({ ...page, body_md: e.target.value })}
                    className="font-mono-accent text-sm"
                  />
                </div>
              )}

              <div className="flex items-center justify-end gap-2">
                <Button
                  data-testid="legal-save"
                  onClick={save}
                  disabled={saving}
                  className="bg-black hover:bg-[#0055FF] text-white"
                >
                  {saving ? "Saving…" : "Save changes"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ----- Main dashboard -----
export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="badge-mono text-neutral-500 mb-2">{isAdmin ? "Admin console" : "Partner console"}</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter">Welcome, {user?.name}</h1>
          <p className="text-neutral-600 mt-2">Manage products, review leads and monitor attribution.</p>
        </div>
        <Badge variant="secondary" className="badge-mono">
          <Users2 className="w-3.5 h-3.5 mr-1.5" /> {user?.email}
        </Badge>
      </div>

      <Tabs defaultValue="analytics" orientation="vertical" className="flex flex-col lg:flex-row gap-6">
        <TabsList
          data-testid="dashboard-tabs"
          className="flex lg:flex-col flex-wrap lg:flex-nowrap lg:w-56 lg:sticky lg:top-6 self-start h-auto bg-neutral-50 border border-black/5 rounded-xl p-2 gap-1 lg:items-stretch"
        >
          <TabsTrigger value="analytics" data-testid="tab-analytics" className="lg:justify-start lg:w-full">Analytics</TabsTrigger>
          <TabsTrigger value="clicks" data-testid="tab-clicks" className="lg:justify-start lg:w-full">Clicks</TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products" className="lg:justify-start lg:w-full">Products</TabsTrigger>
          <TabsTrigger value="cashback" data-testid="tab-cashback" className="lg:justify-start lg:w-full">Cashback rates</TabsTrigger>
          <TabsTrigger value="leads" data-testid="tab-leads" className="lg:justify-start lg:w-full">Leads</TabsTrigger>
          <TabsTrigger value="campaigns" data-testid="tab-campaigns" className="lg:justify-start lg:w-full">Campaigns / UTM</TabsTrigger>
          {isAdmin && <TabsTrigger value="callbacks" data-testid="tab-callbacks" className="lg:justify-start lg:w-full">Callbacks</TabsTrigger>}
          {isAdmin && <TabsTrigger value="customers" data-testid="tab-customers" className="lg:justify-start lg:w-full">Customers</TabsTrigger>}
          {isAdmin && <TabsTrigger value="referrals" data-testid="tab-referrals" className="lg:justify-start lg:w-full">Referrals</TabsTrigger>}
          {isAdmin && <TabsTrigger value="invites" data-testid="tab-invites" className="lg:justify-start lg:w-full">Invites</TabsTrigger>}
          {isAdmin && <TabsTrigger value="legal" data-testid="tab-legal" className="lg:justify-start lg:w-full">Legal</TabsTrigger>}
          {isAdmin && <TabsTrigger value="partners" data-testid="tab-partners" className="lg:justify-start lg:w-full">Partners</TabsTrigger>}
          {isAdmin && <TabsTrigger value="url-health" data-testid="tab-url-health" className="lg:justify-start lg:w-full">URL health</TabsTrigger>}
          {isAdmin && <TabsTrigger value="developer" data-testid="tab-developer" className="lg:justify-start lg:w-full">Developer</TabsTrigger>}
        </TabsList>
        <div className="flex-1 min-w-0">
        <TabsContent value="analytics"><Analytics /></TabsContent>
        <TabsContent value="clicks"><ClicksSection /></TabsContent>
        <TabsContent value="products"><ProductsSection /></TabsContent>
        <TabsContent value="cashback"><CashbackSection /></TabsContent>
        <TabsContent value="leads"><LeadsSection /></TabsContent>
        <TabsContent value="campaigns"><CampaignsSection /></TabsContent>
        {isAdmin && <TabsContent value="callbacks"><CallbacksSection /></TabsContent>}
        {isAdmin && <TabsContent value="customers"><CustomersSection /></TabsContent>}
        {isAdmin && <TabsContent value="referrals"><ReferralsSection /></TabsContent>}
        {isAdmin && <TabsContent value="invites"><InvitesSection /></TabsContent>}
        {isAdmin && <TabsContent value="legal"><LegalSection /></TabsContent>}
        {isAdmin && <TabsContent value="partners"><PartnersSection /></TabsContent>}
        {isAdmin && <TabsContent value="url-health"><UrlHealthSection /></TabsContent>}
        {isAdmin && <TabsContent value="developer"><DeveloperSection /></TabsContent>}
        </div>
      </Tabs>
    </div>
  );
}


// =====================================================================
// CASHBACK RATES — admin/partner grid for inline editing
// =====================================================================
function CashbackSection() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [savedId, setSavedId] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/products/mine/list")
      .then((r) => setRows(r.data))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const update = (id, patch) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const save = async (id) => {
    setSavingId(id);
    try {
      const row = rows.find((r) => r.id === id);
      const { data } = await api.patch(`/products/${id}/cashback`, {
        cashback_rate: row.cashback_rate || null,
        cashback_note: row.cashback_note || null,
        active: row.active,
      });
      update(id, data);
      setSavedId(id);
      setTimeout(() => setSavedId(null), 1500);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSavingId(null);
    }
  };

  const filtered = rows.filter((r) => {
    if (filter === "all") return true;
    if (filter === "missing") return !r.cashback_rate;
    if (filter === "banking") return r.type === "banking";
    if (filter === "ecom") return r.type === "ecom";
    return true;
  });

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#0055FF]/10 text-[#0055FF] grid place-items-center">
            <Wallet className="w-4 h-4" />
          </div>
          <div>
            <div className="font-display text-xl font-semibold tracking-tight">Cashback rates</div>
            <div className="text-xs text-neutral-500">Free-text — displayed on each product card, e.g. &ldquo;Up to 8%&rdquo; or &ldquo;Flat ₹200&rdquo;</div>
          </div>
        </div>
        <div className="ml-auto flex gap-2">
          {["all", "missing", "banking", "ecom"].map((f) => (
            <button
              key={f}
              data-testid={`cashback-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 rounded-full text-xs uppercase tracking-[0.1em] border transition-colors ${
                filter === f ? "bg-black text-white border-black" : "bg-white border-black/10 hover:border-black"
              }`}
            >
              {f}
              {f === "missing" && <span className="ml-1 text-[10px] opacity-70">({rows.filter((r) => !r.cashback_rate).length})</span>}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-neutral-500 py-8">Loading products…</div>
      ) : (
        <div className="bg-white border border-black/10 rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[26%]">Product</TableHead>
                <TableHead>Network</TableHead>
                <TableHead className="w-[24%]">Cashback rate</TableHead>
                <TableHead className="w-[24%]">Note / T&C</TableHead>
                <TableHead>Active</TableHead>
                <TableHead className="w-[100px] text-right">Save</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => {
                const host = (() => { try { return new URL(r.redirect_url || "https://x").hostname.replace(/^www\./, "").split(".").slice(-2, -1)[0] || "-"; } catch { return "-"; } })();
                return (
                  <TableRow key={r.id} data-testid={`cashback-row-${r.id}`}>
                    <TableCell>
                      <div className="font-medium">{r.title}</div>
                      <div className="text-[11px] text-neutral-500">{r.provider} · {r.category}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="badge-mono">{host}</Badge>
                    </TableCell>
                    <TableCell>
                      <Input
                        data-testid={`cashback-rate-${r.id}`}
                        value={r.cashback_rate || ""}
                        onChange={(e) => update(r.id, { cashback_rate: e.target.value })}
                        placeholder="Up to 8%"
                        className="h-9"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        data-testid={`cashback-note-${r.id}`}
                        value={r.cashback_note || ""}
                        onChange={(e) => update(r.id, { cashback_note: e.target.value })}
                        placeholder="Excludes gift cards"
                        className="h-9"
                      />
                    </TableCell>
                    <TableCell>
                      <button
                        data-testid={`cashback-active-${r.id}`}
                        onClick={() => update(r.id, { active: !r.active })}
                        className={`px-2 py-1 rounded-full text-[10px] uppercase tracking-[0.1em] ${
                          r.active ? "bg-[#00A36C]/10 text-[#00A36C]" : "bg-neutral-100 text-neutral-500"
                        }`}
                      >
                        {r.active ? "Active" : "Off"}
                      </button>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        data-testid={`cashback-save-${r.id}`}
                        onClick={() => save(r.id)}
                        disabled={savingId === r.id}
                        variant={savedId === r.id ? "default" : "outline"}
                        className={savedId === r.id ? "bg-[#00A36C] hover:bg-[#00A36C] text-white" : ""}
                      >
                        {savedId === r.id ? <><Check className="w-3.5 h-3.5 mr-1" />Saved</> : <><Save className="w-3.5 h-3.5 mr-1" />Save</>}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

// =====================================================================
// CLICKS — full URL + customer snapshot + network (already backend-ready)
// =====================================================================
function ClicksSection() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [referralsOnly, setReferralsOnly] = useState(false);
  const [copied, setCopied] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/clicks", {
      params: {
        limit: 200,
        q: q || undefined,
        referrals_only: referralsOnly || undefined,
      },
    })
      .then((r) => setRows(r.data))
      .finally(() => setLoading(false));
  }, [q, referralsOnly]);
  useEffect(() => { load(); }, [load]);

  const copy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 1200);
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-8 h-8 rounded-lg bg-neutral-100 grid place-items-center">
          <MousePointerClick className="w-4 h-4" />
        </div>
        <div>
          <div className="font-display text-xl font-semibold tracking-tight">Click log</div>
          <div className="text-xs text-neutral-500">Every affiliate URL generated · matched postback fires against click_id</div>
        </div>
        <div className="ml-auto flex gap-2 items-center">
          <label className="flex items-center gap-1.5 text-xs text-neutral-600 cursor-pointer select-none">
            <input
              type="checkbox"
              data-testid="clicks-referrals-only"
              checked={referralsOnly}
              onChange={(e) => setReferralsOnly(e.target.checked)}
              className="h-3.5 w-3.5"
            />
            Referrals only
          </label>
          <Input
            data-testid="clicks-search"
            placeholder="Search click_id, product, email, phone, sharer…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-9 w-72"
          />
          <Button variant="outline" size="sm" onClick={load} data-testid="clicks-reload">Reload</Button>
        </div>
      </div>

      {loading ? (
        <div className="text-neutral-500 py-8">Loading…</div>
      ) : (
        <div className="bg-white border border-black/10 rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Click ID</TableHead>
                <TableHead>Product</TableHead>
                <TableHead>Sharer</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Network</TableHead>
                <TableHead>UTM</TableHead>
                <TableHead>Tracked URL</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((c) => (
                <TableRow key={c.id} data-testid={`click-row-${c.id}`}>
                  <TableCell className="text-xs text-neutral-500 whitespace-nowrap">
                    {c.created_at ? new Date(c.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "-"}
                  </TableCell>
                  <TableCell><span className="font-mono-accent text-xs">{c.click_id || "-"}</span></TableCell>
                  <TableCell>
                    <div className="font-medium text-sm">{c.product_title || "-"}</div>
                    <div className="text-[11px] text-neutral-500">{c.product_provider}</div>
                  </TableCell>
                  <TableCell>
                    {c.sharer_id ? (
                      <div className="inline-flex items-center gap-1">
                        <Share2 className="w-3 h-3 text-[#0055FF]" />
                        <span className="font-mono-accent text-[11px] text-[#0055FF]">{c.sharer_id}</span>
                      </div>
                    ) : <span className="text-neutral-300 text-xs">direct</span>}
                  </TableCell>
                  <TableCell><span className="font-mono-accent text-xs">{c.user_id || "-"}</span></TableCell>
                  <TableCell>
                    {c.customer_id ? (
                      <div>
                        <div className="text-sm">{c.customer_name || "—"}</div>
                        <div className="text-[11px] text-neutral-500">{c.customer_email || c.customer_phone || ""}</div>
                      </div>
                    ) : <span className="text-neutral-300 text-xs">anonymous</span>}
                  </TableCell>
                  <TableCell>
                    {c.network && <Badge variant="outline" className="badge-mono">{c.network}</Badge>}
                  </TableCell>
                  <TableCell>
                    <div className="text-xs">{c.utm_source || "-"}</div>
                    {c.utm_campaign && <div className="text-[10px] text-neutral-500">{c.utm_campaign}</div>}
                  </TableCell>
                  <TableCell>
                    {c.tracked_redirect_url ? (
                      <div className="flex items-center gap-1 max-w-[380px]">
                        <div className="text-[11px] font-mono-accent truncate text-neutral-600" title={c.tracked_redirect_url}>
                          {c.tracked_redirect_url}
                        </div>
                        <button
                          onClick={() => copy(c.tracked_redirect_url, c.id)}
                          data-testid={`copy-url-${c.id}`}
                          className="text-neutral-400 hover:text-black flex-shrink-0"
                          title="Copy full URL"
                        >
                          {copied === c.id ? <Check className="w-3.5 h-3.5 text-[#00A36C]" /> : <Copy className="w-3.5 h-3.5" />}
                        </button>
                        <a
                          href={c.tracked_redirect_url}
                          target="_blank" rel="noopener noreferrer"
                          className="text-neutral-400 hover:text-black flex-shrink-0"
                          title="Open URL"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    ) : <span className="text-neutral-300 text-xs">no URL</span>}
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center text-neutral-400 py-8">No clicks yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

// =====================================================================
// CUSTOMERS — identified customers with their HITCH-USER slug
// =====================================================================
function CustomersSection() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/customers", { params: { limit: 500 } }).then((r) => setRows(r.data)).finally(() => setLoading(false));
  }, []);
  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-8 h-8 rounded-lg bg-neutral-100 grid place-items-center">
          <UserPlus2 className="w-4 h-4" />
        </div>
        <div>
          <div className="font-display text-xl font-semibold tracking-tight">Identified customers</div>
          <div className="text-xs text-neutral-500">Visitors who shared their mobile / email — their HITCH-USER id is used as p2 on future clicks</div>
        </div>
      </div>
      {loading ? (
        <div className="text-neutral-500 py-8">Loading…</div>
      ) : (
        <div className="bg-white border border-black/10 rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Identified on</TableHead>
                <TableHead>HITCH user id</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>IP</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((c) => (
                <TableRow key={c.id} data-testid={`customer-row-${c.id}`}>
                  <TableCell className="text-xs text-neutral-500 whitespace-nowrap">
                    {c.created_at ? new Date(c.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "-"}
                  </TableCell>
                  <TableCell><span className="font-mono-accent text-xs">{c.hitch_user_id || "-"}</span></TableCell>
                  <TableCell>{c.name || "—"}</TableCell>
                  <TableCell>{c.phone || "—"}</TableCell>
                  <TableCell>{c.email || "—"}</TableCell>
                  <TableCell className="text-xs text-neutral-500">{c.ip || "-"}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-neutral-400 py-8">No identified customers yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

// =====================================================================
// REFERRALS — admin view of every sharer + their recipients
// =====================================================================
function ReferralsSection() {
  const [data, setData] = useState({ summary: {}, sharers: [] });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/referrals")
      .then((r) => setData(r.data || { summary: {}, sharers: [] }))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = (sid) => setExpanded((prev) => ({ ...prev, [sid]: !prev[sid] }));

  const sharers = data.sharers || [];
  const summary = data.summary || {};

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-8 h-8 rounded-lg bg-neutral-100 grid place-items-center">
          <Share2 className="w-4 h-4" />
        </div>
        <div>
          <div className="font-display text-xl font-semibold tracking-tight">Referrals</div>
          <div className="text-xs text-neutral-500">Every shared link · who opened it · what they entered</div>
        </div>
        <div className="ml-auto">
          <Button variant="outline" size="sm" onClick={load} data-testid="referrals-reload">Reload</Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="bg-white border border-black/10 rounded-lg p-4">
          <div className="text-[11px] uppercase tracking-[0.15em] text-neutral-500">Sharers</div>
          <div className="font-display text-2xl font-semibold mt-1" data-testid="referrals-total-sharers">{summary.total_sharers ?? 0}</div>
        </div>
        <div className="bg-white border border-black/10 rounded-lg p-4">
          <div className="text-[11px] uppercase tracking-[0.15em] text-neutral-500">Referral clicks</div>
          <div className="font-display text-2xl font-semibold mt-1" data-testid="referrals-total-clicks">{summary.total_referral_clicks ?? 0}</div>
        </div>
        <div className="bg-white border border-black/10 rounded-lg p-4">
          <div className="text-[11px] uppercase tracking-[0.15em] text-neutral-500">Identified recipients</div>
          <div className="font-display text-2xl font-semibold mt-1" data-testid="referrals-identified">{summary.identified_recipients ?? 0}</div>
        </div>
      </div>

      {loading ? (
        <div className="text-neutral-500 py-8">Loading…</div>
      ) : sharers.length === 0 ? (
        <div className="bg-white border border-black/10 rounded-lg p-10 text-center text-neutral-400 text-sm">
          No referral clicks yet. Once someone opens a shared link with a <span className="font-mono-accent">?ref=</span> tag, they will appear here.
        </div>
      ) : (
        <div className="bg-white border border-black/10 rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-6"></TableHead>
                <TableHead>Sharer</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead className="text-right">Clicks</TableHead>
                <TableHead className="text-right">Unique recipients</TableHead>
                <TableHead>First</TableHead>
                <TableHead>Last</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sharers.map((s) => {
                const open = !!expanded[s.sharer_id];
                return [
                  <TableRow key={s.sharer_id} data-testid={`referral-row-${s.sharer_id}`} className="cursor-pointer hover:bg-neutral-50" onClick={() => toggle(s.sharer_id)}>
                    <TableCell>
                      {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </TableCell>
                    <TableCell>
                      <span className="font-mono-accent text-xs text-[#0055FF]">{s.sharer_id}</span>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">{s.sharer_name || "—"}</div>
                      <div className="text-[11px] text-neutral-500">{s.sharer_phone || s.sharer_email || "not identified"}</div>
                    </TableCell>
                    <TableCell className="text-right font-medium">{s.clicks}</TableCell>
                    <TableCell className="text-right">{s.unique_recipients}</TableCell>
                    <TableCell className="text-xs text-neutral-500 whitespace-nowrap">
                      {s.first_click_at ? new Date(s.first_click_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "-"}
                    </TableCell>
                    <TableCell className="text-xs text-neutral-500 whitespace-nowrap">
                      {s.last_click_at ? new Date(s.last_click_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "-"}
                    </TableCell>
                  </TableRow>,
                  open ? (
                    <TableRow key={`${s.sharer_id}-details`}>
                      <TableCell colSpan={7} className="bg-neutral-50/60 p-0">
                        <div className="p-4">
                          <div className="text-[11px] uppercase tracking-[0.15em] text-neutral-500 mb-2">Recipients ({s.recipients.length})</div>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>When</TableHead>
                                <TableHead>Product</TableHead>
                                <TableHead>Recipient</TableHead>
                                <TableHead>Phone</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead>Click ID</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {s.recipients.map((r) => (
                                <TableRow key={r.id} data-testid={`recipient-row-${r.id}`}>
                                  <TableCell className="text-xs text-neutral-500 whitespace-nowrap">
                                    {r.created_at ? new Date(r.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "-"}
                                  </TableCell>
                                  <TableCell>
                                    <div className="text-sm">{r.product_title || "-"}</div>
                                    <div className="text-[11px] text-neutral-500">{r.product_provider}</div>
                                  </TableCell>
                                  <TableCell>{r.recipient_name || "—"}</TableCell>
                                  <TableCell>{r.recipient_phone || <span className="text-neutral-300 text-xs">not entered</span>}</TableCell>
                                  <TableCell className="text-xs">{r.recipient_email || "—"}</TableCell>
                                  <TableCell><span className="font-mono-accent text-xs">{r.click_id || "-"}</span></TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : null,
                ];
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

